/** Pass 6.0c — plain-text substitution of Slack's embedded markup
 *  syntax, for surfaces that need a string (broadcast header
 *  preview, search result snippets, tooltips). The message body
 *  renderer at lib/slack-text.tsx already does the React-node
 *  version (mention chips, channel chips, links); this is the
 *  string-only twin.
 *
 *  Supported patterns (Slack's official Mrkdwn escape set, minus
 *  the rich-text mrkdwn like *bold*):
 *
 *    <@USER_ID>                 → @display_name (or @unknown-user)
 *    <#CHANNEL_ID|name>         → #name
 *    <#CHANNEL_ID>              → #channel_label (looked up in
 *                                 ``conversations``) or #CHANNEL_ID
 *    <!here>, <!channel>, <!everyone>
 *                               → @here / @channel / @everyone
 *    <!subteam^GROUP_ID|name>   → @name (group name preferred)
 *    <URL|display_text>         → display_text
 *    <URL>                      → URL
 *
 *  Anything that doesn't match a pattern is left as literal text.
 *  Never returns the raw <@…> form for a user — the fallback is
 *  "@unknown-user" so the operator sees a placeholder, not a
 *  visible internal Slack id.
 */

import type { ApiConversation, ApiUser } from "./api";

// Same shape as lib/slack-text.tsx's ESCAPE_RE but with the
// !subteam^… form added (the rich renderer doesn't have it yet;
// the plain-text helper handles it because preview surfaces have
// shown bare <!subteam^…> in the wild for user-group mentions).
const ESCAPE_RE =
  /<(?:@(U[A-Z0-9]+)|#([CG][A-Z0-9]+)(?:\|([^>]+))?|!subteam\^[A-Z0-9]+(?:\|([^>]+))?|(https?:\/\/[^|>\s]+)(?:\|([^>]+))?|!(here|channel|everyone))>/g;

function resolveUserDisplayName(
  userID: string,
  users: Record<string, ApiUser>,
): string {
  const u = users[userID];
  if (!u) return "unknown-user";
  // Same precedence as slack-text.tsx's resolveDisplayName, sans
  // raw-userID fallback — we want a stable placeholder if the
  // user dict doesn't have an entry.
  const name = u.real_name || u.display_name || u.name;
  return (typeof name === "string" && name.length > 0)
    ? name
    : "unknown-user";
}

function resolveChannelLabel(
  channelID: string,
  fromName: string | undefined,
  conversations: ApiConversation[],
): string {
  if (fromName && fromName.length > 0) return fromName;
  const match = conversations.find((c) => c.id === channelID);
  return match ? match.label : channelID;
}

/** Substitute every Slack markup pattern in ``text`` with an
 *  operator-readable plain-text equivalent. Returns a string —
 *  use this for any surface that can't render React (tooltips,
 *  preview labels, telemetry). */
export function formatSlackTextPlain(
  text: string,
  users: Record<string, ApiUser>,
  conversations: ApiConversation[] = [],
): string {
  if (!text) return "";
  // Reset regex state — globals carry lastIndex across calls.
  ESCAPE_RE.lastIndex = 0;
  return text.replace(ESCAPE_RE, (match, userID, channelID, channelName, subteamName, url, linkText, broadcast) => {
    if (userID) return `@${resolveUserDisplayName(userID, users)}`;
    if (channelID) {
      return `#${resolveChannelLabel(channelID, channelName, conversations)}`;
    }
    if (subteamName) return `@${subteamName}`;
    if (url) return linkText && linkText.length > 0 ? linkText : url;
    if (broadcast) return `@${broadcast}`;
    return match;
  });
}
